import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import QRScanner from './QRScanner';
import CameraQRScanner from './CameraQRScanner';
import axios from 'axios';
import { Html5QrcodeScanner } from 'html5-qrcode';
import QRCode from 'react-qr-code';

const PanitiaDashboard: React.FC = () => {
  const { panitia, logoutPanitia } = useAuth();
  const navigate = useNavigate();
  
  // Camera Scanner Component using html5-qrcode
  const CameraScanner: React.FC = () => {
    const [isScanning, setIsScanning] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const scannerRef = useRef<Html5QrcodeScanner | null>(null);

    const startScanner = () => {
      if (isScanning) return;
      
      try {
        // Clear any existing scanner
        if (scannerRef.current) {
          scannerRef.current.clear();
          scannerRef.current = null;
        }

        const scanner = new Html5QrcodeScanner(
          "qr-reader-camera", // Use unique ID
          { 
            fps: 10, 
            qrbox: { width: 250, height: 250 },
            aspectRatio: 1.0,
            supportedScanTypes: []
          },
          false
        );

        scanner.render((decodedText: string) => {
          console.log('QR Code detected:', decodedText);
          handleScanSuccess({ message: decodedText });
          stopScanner();
        }, (error: any) => {
          console.error('Scanner error:', error);
          // Only handle fatal errors, ignore scanning errors
          if (error.name === 'NoQRCodeFoundError' || 
              error.name === 'NotFoundException' ||
              error.name === 'NotAllowedError') {
            return;
          }
          
          // Handle camera permission errors
          if (error.name === 'NotAllowedError' || error.message?.includes('permission')) {
            setError('Izin kamera ditolak. Silakan izinkan akses kamera dan coba lagi.');
            stopScanner();
            return;
          }
          
          // Handle other fatal errors
          if (error.name === 'NotSupportedError' || error.name === 'NotReadableError') {
            setError('Kamera tidak tersedia atau tidak dapat diakses. Silakan coba dengan browser lain.');
            stopScanner();
            return;
          }
          
          // For other errors, show message but don't stop scanner
          console.warn('Scanner warning:', error.message);
        });

        scannerRef.current = scanner;
        setIsScanning(true);
        setError(null);
      } catch (err: any) {
        console.error('Failed to start scanner:', err);
        setError('Gagal memulai scanner. Silakan refresh halaman dan coba lagi.');
        setIsScanning(false);
      }
    };

    const stopScanner = () => {
      try {
        if (scannerRef.current) {
          scannerRef.current.clear();
          scannerRef.current = null;
        }
        setIsScanning(false);
        setError(null);
      } catch (err) {
        console.error('Error stopping scanner:', err);
      }
    };

    // Cleanup on unmount
    useEffect(() => {
      return () => {
        stopScanner();
      };
    }, []);

    return (
      <div>
        <h2 className="text-lg font-semibold mb-4">Scan QR Code dengan Kamera</h2>
        
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <span className="text-red-800 text-sm">{error}</span>
            </div>
          </div>
        )}
        
        <div id="qr-reader-camera" className="w-full max-w-md mx-auto"></div>
        <div className="mt-4 flex gap-2 justify-center">
          {!isScanning ? (
            <button
              onClick={startScanner}
              className="bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
            >
              Aktifkan Kamera
            </button>
          ) : (
            <button
              onClick={stopScanner}
              className="bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
            >
              Matikan Kamera
            </button>
          )}
        </div>
        <p className="text-sm text-gray-600 mt-2 text-center">
          Arahkan kamera ke QR Code untuk melakukan scan
        </p>
      </div>
    );
  };
  const [activeTab, setActiveTab] = useState('dashboard');
  const [checkinData, setCheckinData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  // New state for sorting and filtering
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [showCheckedIn, setShowCheckedIn] = useState<boolean | null>(null); // null = all, true = checked in, false = not checked in
  const [filteredPesertaList, setFilteredPesertaList] = useState<any[]>([]);

  const [scanModalOpen, setScanModalOpen] = useState(false);
  const [scannedData, setScannedData] = useState<any>(null);
  const [scannedRaw, setScannedRaw] = useState<string | null>(null);
  const [scanLoading, setScanLoading] = useState(false);
  const [pesertaInfo, setPesertaInfo] = useState<any>(null);
  const [pendingCheckin, setPendingCheckin] = useState<{qr: string, status: string, catatan: string} | null>(null);
  const [checkinLoading, setCheckinLoading] = useState(false);
  const [confirmStatus, setConfirmStatus] = useState('hadir');
  const [confirmCatatan, setConfirmCatatan] = useState('');

  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [qrModalValue, setQrModalValue] = useState<string | null>(null);

  const handleLogout = () => {
    logoutPanitia();
    navigate('/panitia-login');
  };

  const processPesertaList = useCallback((pesertaList: any[]) => {
    if (!pesertaList) return;

    let filtered = [...pesertaList];

    // Apply check-in filter
    if (showCheckedIn !== null) {
      if (showCheckedIn) {
        // Show only checked in participants
        filtered = filtered.filter(peserta => peserta.total_checkin > 0);
      } else {
        // Show only not checked in participants
        filtered = filtered.filter(peserta => peserta.total_checkin === 0);
      }
    }

    // Apply sorting
    filtered.sort((a, b) => {
      const aTime = a.last_checkin ? new Date(a.last_checkin).getTime() : 0;
      const bTime = b.last_checkin ? new Date(b.last_checkin).getTime() : 0;
      
      if (sortOrder === 'asc') {
        return aTime - bTime;
      } else {
        return bTime - aTime;
      }
    });

    setFilteredPesertaList(filtered);
  }, [showCheckedIn, sortOrder]);

  const loadCheckinData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/get-checkin-data.php');
      if (response.data.success) {
        setCheckinData(response.data.data);
        processPesertaList(response.data.data.peserta_list);
      }
    } catch (error) {
      console.error('Error loading check-in data:', error);
    } finally {
      setLoading(false);
    }
  }, [processPesertaList]);

  // Load check-in data when component mounts and when tab changes
  useEffect(() => {
    if (activeTab === 'peserta') {
      loadCheckinData();
    }
  }, [activeTab, loadCheckinData]);

  // Load check-in data for dashboard statistics
  useEffect(() => {
    if (activeTab === 'dashboard') {
      loadCheckinData();
    }
  }, [activeTab, loadCheckinData]);

  // Update filtered list when sort or filter changes
  useEffect(() => {
    if (checkinData && checkinData.peserta_list) {
      processPesertaList(checkinData.peserta_list);
    }
  }, [sortOrder, showCheckedIn, checkinData, processPesertaList]);

  const handleScanSuccess = async (data: any) => {
    const qrCode = data.message;
    setScannedRaw(qrCode);
    
    // Reset form values
    setConfirmStatus('hadir');
    setConfirmCatatan('');
    
    // Ambil informasi peserta terlebih dahulu
    try {
      const response = await axios.post('https://event.petirs.id/backend/api/get-peserta-by-qr.php', { 
        qr_code: qrCode.trim() 
      });
      
      if (response.data.status === 'success') {
        setPesertaInfo(response.data.data);
        setPendingCheckin({ 
          qr: qrCode.trim(), 
          status: 'hadir', 
          catatan: '' 
        });
        setScanModalOpen(true);
      } else {
        setMessage({ type: 'error', text: response.data.message || 'Peserta tidak ditemukan' });
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Gagal mengambil data peserta' });
    }
  };

  // Separate handler for manual input (QRScanner)
  const handleManualInputSuccess = (data: any) => {
    // Don't do anything for manual input - QRScanner handles its own flow
    console.log('Manual input success:', data);
  };

  const handleScanError = (error: string) => {
    setMessage({ type: 'error', text: error });
    setTimeout(() => setMessage(null), 3000);
  };

  const handleCheckin = async () => {
    if (!pendingCheckin) return;
    setCheckinLoading(true);
    try {
      const response = await axios.post('https://event.petirs.id/backend/api/verify-qr.php', {
        qr_code: pendingCheckin.qr,
        id_panitia: panitia?.id_panitia,
        status: confirmStatus,
        catatan: confirmCatatan
      });
      if (response.data.success) {
        const dataToShow = {
          peserta: response.data.data,
          checkin: {
            status: confirmStatus,
            waktu_checkin: response.data.data.waktu_checkin,
            panitia_nama: panitia?.nama_panitia || 'Panitia',
            catatan: confirmCatatan
          }
        };
        setScannedData(dataToShow);
        setMessage({ type: 'success', text: 'Checkin berhasil!' });
        setScanModalOpen(false);
        setScannedRaw(null);
        setPesertaInfo(null);
        setPendingCheckin(null);
        setConfirmStatus('hadir');
        setConfirmCatatan('');
        loadCheckinData();
      } else {
        setMessage({ type: 'error', text: response.data.message || 'Verifikasi QR Code gagal' });
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Terjadi kesalahan saat verifikasi QR Code' });
    } finally {
      setCheckinLoading(false);
    }
  };

  const handleCancelScan = () => {
    setScanModalOpen(false);
    setScannedRaw(null);
    setScannedData(null);
    setPesertaInfo(null);
    setPendingCheckin(null);
    setConfirmStatus('hadir');
    setConfirmCatatan('');
  };

  if (!panitia) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Akses Ditolak</h2>
          <p className="text-gray-600">Anda harus login sebagai panitia terlebih dahulu.</p>
          <button
            onClick={() => navigate('/panitia-login')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Login Panitia
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <h1 className="text-xl font-semibold text-gray-900">
                Dashboard Panitia - Petirs
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-700">
                Selamat datang, {panitia.nama_panitia}
              </span>
              <button
                onClick={handleLogout}
                className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Navigation Tabs */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'dashboard'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Dashboard
            </button>
            <button
              onClick={() => setActiveTab('profile')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'profile'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Profil
            </button>
            <button
              onClick={() => setActiveTab('qr-scanner')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'qr-scanner'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Input Manual
            </button>
            <button
              onClick={() => setActiveTab('camera-scanner')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'camera-scanner'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Scan Kamera
            </button>
            <button
              onClick={() => setActiveTab('peserta')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'peserta'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Data Peserta
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {activeTab === 'dashboard' && (
          <div className="px-4 py-6 sm:px-0">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                        </svg>
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Total Peserta
                        </dt>
                        <dd className="text-lg font-medium text-gray-900">
                          {checkinData ? checkinData.statistics.total_peserta : '-'}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Peserta Hadir
                        </dt>
                        <dd className="text-lg font-medium text-gray-900">
                          {checkinData ? checkinData.statistics.hadir : '-'}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-yellow-500 rounded-md flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          Belum Hadir
                        </dt>
                        <dd className="text-lg font-medium text-gray-900">
                          {checkinData ? checkinData.statistics.belum_checkin : '-'}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  Aktivitas Check-in Terbaru
                </h3>
                {checkinData && checkinData.checkin_list.length > 0 ? (
                <div className="space-y-4">
                    {checkinData.checkin_list.slice(0, 5).map((item: any) => (
                      <div key={item.id_checkin} className="flex items-center space-x-3">
                        <div className={`w-2 h-2 rounded-full ${
                          item.status === 'hadir' 
                            ? 'bg-green-400'
                            : item.status === 'terlambat'
                            ? 'bg-yellow-400'
                            : 'bg-red-400'
                        }`}></div>
                        <span className="text-sm text-gray-600">
                          {item.nama_pendaftar} - {item.status === 'hadir' ? 'Hadir' : 
                           item.status === 'terlambat' ? 'Terlambat' : 'Tidak Hadir'}
                        </span>
                        <span className="text-xs text-gray-400">
                          {new Date(item.waktu_checkin).toLocaleString('id-ID')}
                        </span>
                  </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-gray-500">Belum ada aktivitas check-in</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'profile' && (
          <div className="px-4 py-6 sm:px-0">
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-6">
                  Profil Panitia
                </h3>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">ID Panitia</label>
                    <p className="mt-1 text-sm text-gray-900">{panitia.id_panitia}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Username</label>
                    <p className="mt-1 text-sm text-gray-900">{panitia.username}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Nama Lengkap</label>
                    <p className="mt-1 text-sm text-gray-900">{panitia.nama_panitia}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <p className="mt-1 text-sm text-gray-900">{panitia.email}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">No. HP</label>
                    <p className="mt-1 text-sm text-gray-900">{panitia.no_hp}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Role</label>
                    <span className={`mt-1 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      panitia.role === 'admin' 
                        ? 'bg-red-100 text-red-800' 
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {panitia.role === 'admin' ? 'Administrator' : 'Panitia'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'qr-scanner' && (
          <div className="px-4 py-6 sm:px-0">
            <div className="max-w-2xl mx-auto">
              {message && (
                <div className={`mb-4 p-4 rounded-md ${
                  message.type === 'success' 
                    ? 'bg-green-50 border border-green-200 text-green-800' 
                    : 'bg-red-50 border border-red-200 text-red-800'
                }`}>
                  {message.text}
                </div>
              )}
              <QRScanner onScanSuccess={handleManualInputSuccess} onScanError={handleScanError} />
            </div>
          </div>
        )}

        {activeTab === 'camera-scanner' && (
          <div className="px-4 py-6 sm:px-0">
            <div className="max-w-2xl mx-auto">
              {message && (
                <div className={`mb-4 p-4 rounded-md ${
                  message.type === 'success' 
                    ? 'bg-green-50 border border-green-200 text-green-800' 
                    : 'bg-red-50 border border-red-200 text-red-800'
                }`}>
                  {message.text}
                </div>
              )}
              <CameraScanner />
              {/* Floating Card/Dialog setelah scan */}
              {scanModalOpen && pendingCheckin && (
                <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-50">
                  <div className="bg-white rounded-xl shadow-xl p-6 w-[400px] max-w-full border border-gray-200">
                    <h3 className="text-base font-semibold mb-3">Konfirmasi Checkin Peserta</h3>
                    {checkinLoading ? (
                      <div className="text-center py-6">
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        <p className="mt-2 text-gray-600">Memproses checkin...</p>
                      </div>
                    ) : (
                      <>
                        <div className="mb-3">
                          <div className="text-sm text-gray-700 mb-1">QR Code: <span className="font-mono">{pendingCheckin.qr}</span></div>
                          {pesertaInfo && (
                            <div className="mt-2 p-2 bg-gray-50 rounded mb-3">
                              <div className="text-sm text-gray-800"><strong>Nama:</strong> {pesertaInfo.nama_pendaftar}</div>
                              <div className="text-sm text-gray-800"><strong>No. Peserta:</strong> {pesertaInfo.no_peserta}</div>
                              <div className="text-sm text-gray-800"><strong>Instansi:</strong> {pesertaInfo.instansi}</div>
                              <div className="text-sm text-gray-800"><strong>Status Menginap:</strong> {pesertaInfo.status_menginap}</div>
                            </div>
                          )}
                          
                          {/* Form untuk status dan catatan */}
                          <div className="space-y-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Status Kehadiran
                              </label>
                              <select
                                value={confirmStatus}
                                onChange={(e) => setConfirmStatus(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              >
                                <option value="hadir">Hadir</option>
                                <option value="terlambat">Terlambat</option>
                                <option value="tidak_hadir">Tidak Hadir</option>
                              </select>
                            </div>
                            
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Catatan (Opsional)
                              </label>
                              <textarea
                                value={confirmCatatan}
                                onChange={(e) => setConfirmCatatan(e.target.value)}
                                placeholder="Catatan tambahan..."
                                rows={2}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2 justify-end">
                          <button
                            onClick={handleCancelScan}
                            className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                            disabled={checkinLoading}
                          >
                            Batal
                          </button>
                          <button
                            onClick={handleCheckin}
                            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                            disabled={checkinLoading}
                          >
                            Checkin
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'peserta' && (
          <div className="px-4 py-6 sm:px-0">
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Data Peserta dengan History Check-in
                  </h3>
                  <button
                    onClick={loadCheckinData}
                    disabled={loading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {loading ? 'Loading...' : 'Refresh'}
                  </button>
                </div>
                
                {/* Sorting and Filtering Controls */}
                {!loading && checkinData && (
                  <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                    <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                      {/* Sort Control */}
                      <div className="flex items-center space-x-2">
                        <label className="text-sm font-medium text-gray-700">Urutkan berdasarkan:</label>
                        <select
                          value={sortOrder}
                          onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
                          className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="desc">Check-in Terbaru</option>
                          <option value="asc">Check-in Terlama</option>
                        </select>
                      </div>

                      {/* Filter Control */}
                      <div className="flex items-center space-x-4">
                        <label className="text-sm font-medium text-gray-700">Filter:</label>
                        <div className="flex items-center space-x-3">
                          <label className="flex items-center">
                            <input
                              type="radio"
                              name="filter"
                              value="all"
                              checked={showCheckedIn === null}
                              onChange={() => setShowCheckedIn(null)}
                              className="mr-2"
                            />
                            <span className="text-sm text-gray-700">Semua</span>
                          </label>
                          <label className="flex items-center">
                            <input
                              type="radio"
                              name="filter"
                              value="checked"
                              checked={showCheckedIn === true}
                              onChange={() => setShowCheckedIn(true)}
                              className="mr-2"
                            />
                            <span className="text-sm text-gray-700">Sudah Check-in</span>
                          </label>
                          <label className="flex items-center">
                            <input
                              type="radio"
                              name="filter"
                              value="not-checked"
                              checked={showCheckedIn === false}
                              onChange={() => setShowCheckedIn(false)}
                              className="mr-2"
                            />
                            <span className="text-sm text-gray-700">Belum Check-in</span>
                          </label>
                        </div>
                      </div>

                      {/* Results Count */}
                      <div className="text-sm text-gray-600">
                        Menampilkan {filteredPesertaList.length} dari {checkinData.peserta_list.length} peserta
                      </div>
                    </div>
                  </div>
                )}
                
                {loading && (
                  <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <p className="mt-2 text-gray-600">Memuat data...</p>
                  </div>
                )}
                
                {!loading && checkinData && (
                  <div className="space-y-6">
                    {/* Statistics */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <div className="text-2xl font-bold text-blue-600">{checkinData.statistics.total_peserta}</div>
                        <div className="text-sm text-blue-600">Total Peserta</div>
                      </div>
                      <div className="bg-green-50 p-4 rounded-lg">
                        <div className="text-2xl font-bold text-green-600">{checkinData.statistics.hadir}</div>
                        <div className="text-sm text-green-600">Hadir</div>
                      </div>
                      <div className="bg-yellow-50 p-4 rounded-lg">
                        <div className="text-2xl font-bold text-yellow-600">{checkinData.statistics.terlambat}</div>
                        <div className="text-sm text-yellow-600">Terlambat</div>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <div className="text-2xl font-bold text-gray-600">{checkinData.statistics.belum_checkin}</div>
                        <div className="text-sm text-gray-600">Belum Check-in</div>
                      </div>
                    </div>

                    {/* Peserta List with History */}
                    {filteredPesertaList && filteredPesertaList.length > 0 ? (
                      <div className="space-y-4">
                        {filteredPesertaList.map((peserta: any) => (
                          <div key={peserta.no_peserta} className="border border-gray-200 rounded-lg p-4">
                            <div className="flex justify-between items-start mb-3">
                              <div className="flex-1">
                                <h4 className="text-lg font-semibold text-gray-900 mb-2">{peserta.nama_pendaftar}</h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                                  <div className="space-y-1">
                                    <div className="text-gray-600">
                                      <span className="font-medium">No. Peserta:</span> {peserta.no_peserta || '-'}
                                    </div>
                                    <div className="text-gray-600">
                                      <span className="font-medium">HP:</span> {peserta.hp || '-'}
                                    </div>
                                  </div>
                                  <div className="space-y-1">
                                    <div className="text-gray-600">
                                      <span className="font-medium">Event:</span> {peserta.nama_event || `Event ${peserta.event}`}
                                    </div>
                                    <div className="text-gray-600">
                                      <span className="font-medium">Paket:</span> {peserta.paket || '-'}
                                    </div>
                                    <div className="text-gray-600">
                                      <span className="font-medium">Instansi:</span> {peserta.nama_instansi || `Instansi ${peserta.instansi}`}
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="text-right ml-4">
                                <div className="bg-blue-50 px-3 py-2 rounded-lg mb-2">
                                  <div className="text-sm text-gray-600">
                                    <span className="font-medium">Total Check-in:</span> 
                                    <span className="ml-1 text-lg font-bold text-blue-600">{peserta.total_checkin}</span>
                                  </div>
                                  {peserta.last_checkin && (
                                    <div className="text-sm text-gray-600 mt-1">
                                      <span className="font-medium">Terakhir:</span><br/>
                                      <span className="text-xs">{new Date(peserta.last_checkin).toLocaleString('id-ID')}</span>
                                    </div>
                                  )}
                                </div>
                                {/* Tombol Cetak QR Code */}
                                <button
                                  className="mt-2 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
                                  onClick={() => { setQrModalValue(peserta.kode); setQrModalOpen(true); }}
                                  disabled={!peserta.kode}
                                >
                                  Cetak QR Code
                                </button>
                              </div>
                            </div>
                            
                            {/* History Check-in */}
                            {peserta.checkin_history && peserta.checkin_history.length > 0 ? (
                              <div className="mt-3">
                                <h5 className="text-sm font-medium text-gray-700 mb-2">History Check-in:</h5>
                                <div className="space-y-2">
                                  {peserta.checkin_history.map((history: any, index: number) => (
                                    <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                                      <div className="flex items-center space-x-3">
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                          history.status === 'hadir' 
                                    ? 'bg-green-100 text-green-800'
                                            : history.status === 'terlambat'
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : 'bg-red-100 text-red-800'
                                }`}>
                                          {history.status === 'hadir' ? 'Hadir' : 
                                           history.status === 'terlambat' ? 'Terlambat' : 'Tidak Hadir'}
                                        </span>
                                        <span className="text-sm text-gray-600">
                                          {history.waktu_checkin ? new Date(history.waktu_checkin).toLocaleString('id-ID') : 'Waktu tidak valid'}
                                        </span>
                                        <span className="text-sm text-gray-600">
                                          Panitia: {history.panitia_nama || 'Tidak diketahui'}
                                        </span>
                                      </div>
                                      {history.catatan && history.catatan.trim() !== '' && (
                                        <span className="text-sm text-gray-500 italic">
                                          "{history.catatan}"
                                </span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ) : (
                              <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
                                <p className="text-sm text-yellow-800">Belum ada history check-in</p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : !loading && checkinData && checkinData.peserta_list && checkinData.peserta_list.length > 0 ? (
                      <div className="text-center py-8">
                        <div className="text-gray-400 mb-4">
                          <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">Tidak ada hasil</h3>
                        <p className="text-gray-500">Tidak ada peserta yang sesuai dengan filter yang dipilih.</p>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <div className="text-gray-400 mb-4">
                          <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">Belum ada data peserta</h3>
                        <p className="text-gray-500">Tidak ada peserta terdaftar untuk event ini.</p>
                      </div>
                    )}
                    </div>
                )}
                
                {!loading && !checkinData && (
                  <div className="text-center py-8">
                    <p className="text-gray-600">Gagal memuat data. Silakan coba refresh.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal QR Code */}
      {qrModalOpen && qrModalValue && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-xs flex flex-col items-center">
            <h3 className="text-lg font-semibold mb-4">QR Code Peserta</h3>
            <QRCode value={qrModalValue} size={180} title="QR Code Peserta" />
            <div className="mt-4 text-xs text-gray-600 break-all">{qrModalValue}</div>
            <button
              className="mt-6 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
              onClick={() => setQrModalOpen(false)}
            >
              Tutup
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PanitiaDashboard; 